import { ImplementationRulesBackfillOutcome } from '@cadstrom/backend/shared';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { Toast } from '#components/toast/toast.js';
import { buildImplementationRulesOperatorPatch } from '#package-store/package-editor/raw-tab/implementation-rules-operator-patch.js';
import { SetAiExtractionContext } from '#screens/ai-workbench/ai-extraction-context.js';
import { useTRPC, useTRPCClient } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';
import { useEventCallback } from '#utils/use-event-callback.js';
import { getImplementationRulesAiExtractionContextValue } from './implementation-rules-ai-extraction-context.js';
import { ImplementationRulesBackfillPhase } from './implementation-rules-backfill-phase.js';
import { createImplementationRulesDraft, toImplementationRules } from './implementation-rules-draft.js';
import { applyImplementationRulesProposal } from './implementation-rules-proposal.js';
import { isSavableImplementationRules } from './implementation-rules-tab-utils.js';
import { ImplementationRulesTabView } from './implementation-rules-tab-view.js';
import { reconcileImplementationRulesDraftAfterSave } from './reconcile-implementation-rules-draft.js';

import type { ImplementationRulesDraft } from './implementation-rules-draft.js';

/** How often the backfill status is re-read while a backfill is running. */
const BACKFILL_POLL_INTERVAL_MS = 10_000;

/** Operator-facing explanation for each outcome that leaves the package without a backfill. */
const WITHHELD_BACKFILL_MESSAGES: Record<
	Exclude<
		ImplementationRulesBackfillOutcome,
		ImplementationRulesBackfillOutcome.Dispatched | ImplementationRulesBackfillOutcome.AlreadyInFlight
	>,
	string
> = {
	[ImplementationRulesBackfillOutcome.SigmaModelNotEligible]:
		'This package\u2019s model type does not produce implementation rules.',
	[ImplementationRulesBackfillOutcome.NoDatasheet]: 'Attach a datasheet before generating rules.',
	[ImplementationRulesBackfillOutcome.DatasheetInvalid]:
		'The attached datasheet failed validation against this part. Replace it, or override the verdict, before generating rules.',
	[ImplementationRulesBackfillOutcome.NotUnderstood]:
		'Package understanding has not produced signals yet. Run package understanding first.',
	[ImplementationRulesBackfillOutcome.HasRules]: 'This package already has implementation rules. Reload to see them.',
	[ImplementationRulesBackfillOutcome.UnderstandingInFlight]:
		'Package understanding is still running. Rules are generated when it finishes.',
};

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/implementation-rules/',
)({
	staticData: {
		navigation: {
			label: 'Package: Implementation rules',
			keywords: ['implementation', 'rules', 'datasheet'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: `${loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : ''}Implementation rules • Package editor • Cadstrom`,
			},
		],
	}),
	component: ImplementationRulesTab,
});

/** Hosts the retained implementation-rule set with autosave-backed operator patches. */
function ImplementationRulesTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();
	const trpcClient = useTRPCClient();
	const addToast = useAddToast();
	const [saveError, setSaveError] = useState<string | undefined>();
	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);
	const backfillStatusOptions = trpc.package.getImplementationRulesBackfillStatus.queryOptions(
		{ packageId: pkg.id },
		{
			refetchOnMount: 'always',
			refetchInterval: (query) => (query.state.data?.inFlight === true ? BACKFILL_POLL_INTERVAL_MS : false),
		},
	);
	const { data: backfillStatus } = useQuery({
		...backfillStatusOptions,
		// The poll that first sees the workflow finished is the moment its rules are on the package,
		// so that same read refreshes the package rather than a separate package poll racing it.
		queryFn: async ({ signal }) => {
			const previous = queryClient.getQueryData(backfillStatusOptions.queryKey);
			const status = await trpcClient.package.getImplementationRulesBackfillStatus.query(
				{ packageId: pkg.id },
				{ signal },
			);
			if (previous?.inFlight === true && !status.inFlight) {
				await queryClient.invalidateQueries({ queryKey: getPackageOptions.queryKey });
			}
			return status;
		},
	});
	const isAwaitingBackfill = backfillStatus?.inFlight === true;
	/** Re-reads the run state and the package, so a run that already finished still lands its rules. */
	const refreshAfterDispatch = () =>
		Promise.all([
			queryClient.invalidateQueries({ queryKey: backfillStatusOptions.queryKey }),
			queryClient.invalidateQueries({ queryKey: getPackageOptions.queryKey }),
		]);
	const { mutate: backfillImplementationRules, isPending: isDispatchingBackfill } = useMutation(
		trpc.package.backfillImplementationRules.mutationOptions({
			onSuccess: (result) => {
				switch (result.outcome) {
					case ImplementationRulesBackfillOutcome.Dispatched: {
						void refreshAfterDispatch();
						addToast(
							<Toast severity='info' title='Generating implementation rules'>
								Rules are being generated from the datasheet. This tab updates when they arrive.
							</Toast>,
						);
						return;
					}
					case ImplementationRulesBackfillOutcome.AlreadyInFlight: {
						void refreshAfterDispatch();
						addToast(
							<Toast severity='info' title='Generation already running'>
								A rules generation for this package is already in progress. This tab updates when it finishes.
							</Toast>,
						);
						return;
					}
					default: {
						addToast(
							<Toast severity='warning' title='Rules not generated'>
								{WITHHELD_BACKFILL_MESSAGES[result.outcome]}
							</Toast>,
						);
					}
				}
			},
			onError: (error) => {
				addToast(
					<Toast severity='error' title='Rules not generated'>
						{error.message}
					</Toast>,
				);
			},
		}),
	);
	const { mutateAsync: updatePackage, isPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (result) =>
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? previous : { ...previous, pkg: result.pkg },
				),
		}),
	);
	const { draftState, setDraftState, setServerState, isDirty, isValid, handleSave, saveAreaContainerRef } = useAutoSave<
		ImplementationRulesDraft | null,
		{}
	>({
		initialState: createImplementationRulesDraft(pkg.implementationRules),
		mutationKeys: trpc.package.update.mutationKey(),
		isValid: isSavableImplementationRules,
		saveFn: async (draft, baseline) => {
			const persistedDraft = toImplementationRules(draft);
			const persistedBaseline = toImplementationRules(baseline);
			if (persistedDraft === null || persistedBaseline === null) {
				throw new Error('Complete required rule, citation, and section fields before saving.');
			}
			const patch = await buildImplementationRulesOperatorPatch(persistedBaseline, persistedDraft);
			if (patch === undefined) {
				return;
			}
			try {
				const result = await updatePackage({ id: pkg.id, data: { implementationRules: patch } });
				const serverRules = reconcileImplementationRulesDraftAfterSave(
					result.pkg.implementationRules,
					persistedDraft,
					draft,
				);
				setSaveError(undefined);
				setServerState(serverRules);
				setDraftState((currentDraft) =>
					reconcileImplementationRulesDraftAfterSave(result.pkg.implementationRules, persistedDraft, currentDraft),
				);
			} catch (error) {
				setSaveError(error instanceof Error ? error.message : 'Implementation rules could not be saved.');
				throw error;
			}
		},
	});

	const getDirectiveTarget = useEventCallback((ruleId: string) => {
		for (const section of draftState ?? []) {
			const rule = section.rules.find((candidate) => candidate.id === ruleId);
			if (rule !== undefined) {
				return { sectionSlug: section.sectionSlug, text: rule.text };
			}
		}
		return undefined;
	});
	const applyProposalAtomically = useEventCallback(
		(data: object, mode: Parameters<typeof applyImplementationRulesProposal>[2]) => {
			// Validation and application intentionally share this one committed snapshot. JavaScript runs
			// this callback synchronously, so no rule can disappear between the two operations.
			const current = draftState;
			if (current === null) {
				return [{ row: data, reason: 'This package has no active implementation rule set.' }];
			}
			const result = applyImplementationRulesProposal(current, data, mode);
			if (result.rejected.length === 0) {
				setDraftState(result.draft);
			}
			return result.rejected;
		},
	);
	const hasRuleSet = draftState !== null;
	const extractionContextValue = useMemo(
		() =>
			getImplementationRulesAiExtractionContextValue({
				hasRuleSet,
				getDirectiveTarget,
				applyProposalAtomically,
			}),
		[hasRuleSet, getDirectiveTarget, applyProposalAtomically],
	);

	return (
		<>
			<SetAiExtractionContext value={extractionContextValue} />
			<ImplementationRulesTabView
				implementationRules={draftState}
				datasheetFileId={pkg.fileId}
				saveError={saveError}
				onChange={setDraftState}
				onSave={handleSave}
				isSaveDisabled={!isDirty || !isValid}
				saveDisabledReason={
					!isDirty ? 'Make a change before saving.'
					: !isValid ?
						'Complete required rule, citation, and section fields before saving.'
					:	undefined
				}
				isSaving={isPending}
				saveAreaContainerRef={saveAreaContainerRef}
				backfillPhase={
					isAwaitingBackfill ? ImplementationRulesBackfillPhase.Awaiting
					: isDispatchingBackfill ?
						ImplementationRulesBackfillPhase.Dispatching
					:	ImplementationRulesBackfillPhase.Idle
				}
				backfillWorkflowUrl={backfillStatus?.workflowUrl ?? null}
				onBackfill={() => backfillImplementationRules({ packageId: pkg.id })}
			/>
		</>
	);
}
